import './coapplicantResidenceRelationshipPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { NO, YES, YesOrNo } from '../../../store/applicantStore/types/YesOrNo';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    hasSameResidenceAsApplicant,
    clearCoapplicantCurrentResidence,
    setHasSameResidenceAsApplicant,
  } = useCoapplicantStore();

  const {
    formState: { isDirty },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: {
      hasSameResidenceAsApplicantValue: hasSameResidenceAsApplicant,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<YesOrNo>): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const hasSameResidenceAsApplicantValue = event.target.value;

    setIsInitialSelection(
      isEmpty(hasSameResidenceAsApplicant) && isValidClickEvent(event),
    );

    setHasSameResidenceAsApplicant(hasSameResidenceAsApplicantValue);
  };

  const formSubmit = (values: FieldValues): void => {
    const { hasSameResidenceAsApplicantValue } = values;

    if (!hasSameResidenceAsApplicantValue) {
      return;
    }

    if (hasSameResidenceAsApplicantValue === NO && isDirty) {
      clearCoapplicantCurrentResidence();
    }

    navigate(next());
  };

  useEffect(() => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="coapplicant-residence-relationship-page"
        sx={theme.pageLayout}
      >
        <header
          data-testid={
            locator.pages.coapplicantResidenceRelationship.header.root
          }
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantResidenceRelationship.header
                .previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.coapplicantResidenceRelationship.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={
              locator.pages.coapplicantResidenceRelationship.progress.root
            }
            value={useWorkflowStore.getState().computed.progress}
            variant="determinate"
          />
        </div>

        <main>
          <form noValidate onSubmit={handleSubmit(formSubmit)}>
            <div className="form-content">
              <h1>{t('CoapplicantResidenceRelationshipPage.title')}</h1>
              <h2>{t('CoapplicantResidenceRelationshipPage.subtitle')}</h2>

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  defaultValue={hasSameResidenceAsApplicant || ''}
                  labelPlacement="below"
                  name="hasSameResidenceAsApplicantValue"
                  options={[
                    {
                      label: t('CoapplicantResidenceRelationshipPage.yes'),
                      value: YES,
                      testId:
                        locator.pages.coapplicantResidenceRelationship.form
                          .radioOption1,
                    },
                    {
                      label: t('CoapplicantResidenceRelationshipPage.no'),
                      value: NO,
                      testId:
                        locator.pages.coapplicantResidenceRelationship.form
                          .radioOption2,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.coapplicantResidenceRelationship.form
                      .radioGroupRoot
                  }
                  variant="inline"
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.coapplicantResidenceRelationship.form
                      .submitButton
                  }
                  disabled={isEmpty(hasSameResidenceAsApplicant)}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('CoapplicantResidenceRelationshipPage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantResidenceRelationship.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantResidenceRelationshipPage';
